import type { JobCreateFormValues } from '@/lib/jobs/jobFormDto';
import { readTermsSelection, type TermsSelection } from '@/lib/terms';
import type { AdditionalContact } from '@/types/contact';
import type { Estimate, EstimateRecurringConfig } from '@/types/estimate';
import type { IntervalUnit, RecurringFrequency } from '@/types/job';

type BillToContactSnapshot = {
  contactName?: string;
  name?: string;
  email?: string | null;
};

/** Match a frozen document bill-to person back to the owner's current contacts. */
export function matchBillToContactId(
  snapshot: BillToContactSnapshot | null | undefined,
  contacts: readonly AdditionalContact[],
): string | undefined {
  const snapshotName = snapshot?.contactName ?? snapshot?.name;
  if (!snapshotName) return undefined;

  return contacts.find(
    (contact) =>
      contact.name === snapshotName && (contact.email ?? null) === (snapshot?.email ?? null),
  )?.id;
}

/** True when the form still carries the source document's frozen wording. */
export function shouldInheritEstimateTerms(selection: TermsSelection, estimate: Estimate): boolean {
  const source = readTermsSelection(estimate);
  return (
    selection.termsMode === source.termsMode &&
    // Order matters: the same entries stacked differently are a changed choice.
    selection.termsTemplateIds.length === source.termsTemplateIds.length &&
    selection.termsTemplateIds.every((id, i) => id === source.termsTemplateIds[i]) &&
    selection.termsAndConditions.trim() === source.termsAndConditions.trim()
  );
}

function dateOnly(value: string | null | undefined): string {
  return value ? (value.split('T')[0] ?? '') : '';
}

function mapFrequency(
  frequency: EstimateRecurringConfig['frequency'] | string | undefined,
): RecurringFrequency {
  switch (frequency) {
    case 'ANNUALLY':
      return 'ANNUAL';
    case 'DAILY':
    case 'WEEKLY':
    case 'BIWEEKLY':
    case 'MONTHLY':
    case 'QUARTERLY':
    case 'SEMIANNUAL':
    case 'ANNUAL':
    case 'CUSTOM':
      return frequency;
    default:
      return 'WEEKLY';
  }
}

function defaultIntervalUnit(frequency: RecurringFrequency): IntervalUnit {
  if (frequency === 'DAILY') return 'DAYS';
  if (frequency === 'MONTHLY' || frequency === 'QUARTERLY' || frequency === 'SEMIANNUAL') {
    return 'MONTHS';
  }
  if (frequency === 'ANNUAL') return 'YEARS';
  return 'WEEKS';
}

function mapEstimateVisitPatterns(
  patterns: EstimateRecurringConfig['visitPatterns'],
): JobCreateFormValues['recurVisitPatterns'] {
  return (patterns ?? []).map((pattern) => ({
    id: pattern.id,
    kind: pattern.kind,
    dayOfWeek: pattern.dayOfWeek,
    dayOfMonth: pattern.dayOfMonth,
    weekOfMonth: pattern.weekOfMonth,
    month: pattern.month,
    startTime: pattern.startTime,
    endTime: pattern.endTime,
    // Intentionally omit technicianId if an older/raw estimate snapshot has
    // one. Conversion must not carry scheduling assignments automatically.
  }));
}

/**
 * Maps an approved estimate snapshot into the editable New Job form. This does
 * not create or schedule anything; it only prepares the draft the dispatcher
 * reviews before submitting the conversion.
 *
 * NOT-542 — the business unit has to be shown, not just carried. Whichever way
 * the estimate's unit was CHOSEN, `EstimatesService.convertToJob()` puts it on
 * the new job when the reviewed draft names no unit of its own: MANUAL_OVERRIDE
 * carries as `businessUnitId`, SELECTED as `selectedBusinessUnitId`. Hydrating
 * only the override left a SELECTED estimate reopening with an empty picker, so
 * the reviewer read "resolve on save", submitted, and the job was filed under
 * the estimate's unit anyway — the form disagreeing with the record it creates.
 *
 * So both are hydrated, and `carriedBusinessUnit` remembers WHICH channel the
 * value arrived on. `buildCreateJobDto` needs that to answer for the reviewer
 * without changing what the unit means: a selection can restate itself, an
 * override cannot (restating `businessUnitId` demands `dispatch:reassign`,
 * which a rep converting their own estimate need not have), and REMOVING the
 * carried unit has to be said out loud or the server puts it back.
 */
export function estimateToJobFormValues(
  estimate: Estimate,
  base: JobCreateFormValues,
): JobCreateFormValues {
  const recurring = estimate.bookingType === 'RECURRING' ? estimate.recurringConfig : null;
  const frequency = mapFrequency(recurring?.frequency);
  const escalation = recurring?.escalation;
  const monthlyDay = recurring?.monthDay;
  // A DERIVED (or unset) estimate unit is deliberately NOT hydrated: the job
  // derives its own from the same job type, and pinning the estimate's answer
  // would turn an inference into a choice.
  const carriedBusinessUnit =
    (estimate.businessUnitSource === 'MANUAL_OVERRIDE' ||
      estimate.businessUnitSource === 'SELECTED') &&
    estimate.businessUnitId
      ? { id: estimate.businessUnitId, source: estimate.businessUnitSource }
      : null;
  const businessUnitWasChosen =
    estimate.businessUnitSource === 'MANUAL_OVERRIDE' || estimate.businessUnitSource === 'SELECTED';

  return {
    ...base,
    issueSummary: estimate.title ?? '',
    contactId: estimate.contactId ?? '',
    billToId: estimate.billToId,
    jobType: (estimate.jobTypeName ?? '') as JobCreateFormValues['jobType'],
    jobTypeId: estimate.jobTypeId ?? '',
    jobTypeName: estimate.jobTypeName ?? '',
    // A MANUAL_OVERRIDE to no unit at all is itself a decision, so an override
    // carrying null stays null rather than falling back to the form's default.
    businessUnitId: businessUnitWasChosen ? (estimate.businessUnitId ?? null) : base.businessUnitId,
    carriedBusinessUnit,
    salesRepId: estimate.salespersonId ?? '',
    street: estimate.serviceAddress?.street ?? '',
    street2: estimate.serviceAddress?.street2 ?? '',
    city: estimate.serviceAddress?.city ?? '',
    state: estimate.serviceAddress?.state ?? '',
    zip: estimate.serviceAddress?.zip ?? '',
    country: 'US',
    scopeOfWork: estimate.scopeOfWork ?? '',
    instructions: estimate.notes ?? '',
    internalNotes: estimate.internalNotes ?? '',
    // The approved document snapshot is authoritative. A Job Type may have
    // changed since approval, so never replace these terms with current defaults.
    terms: readTermsSelection(estimate),
    tagIds: estimate.tags?.map((tag) => tag.tagId) ?? [],
    bookingType: estimate.bookingType,
    // Estimate recurrence is authoritative. Do not let a selected Job Type's
    // flexible pattern or cycle visits leak into the converted series.
    recurPatternMode: 'FIXED',
    recurVisitsPerCycle: '1',
    recurDayConstraintMode: 'ANY',
    recurAllowedWeekDays: [],
    recurFrequency: frequency,
    recurStartDate: dateOnly(recurring?.startDate),
    recurVisitStartTime: recurring?.visitStartTime ?? '09:00',
    recurVisitEndTime: recurring?.visitEndTime ?? '',
    recurVisitEndOverride: Boolean(recurring?.visitEndTime),
    recurVisitPatterns: mapEstimateVisitPatterns(recurring?.visitPatterns),
    recurVisitGranularity: recurring?.visitGranularity,
    recurEndType: recurring?.endType ?? 'INDEFINITE',
    recurEndAfterCount: recurring?.endAfterCount ? String(recurring.endAfterCount) : '',
    recurEndOnDate: dateOnly(recurring?.endOnDate),
    recurInterval: String(recurring?.interval ?? 1),
    recurIntervalUnit:
      (recurring?.intervalUnit as IntervalUnit | undefined) ?? defaultIntervalUnit(frequency),
    recurWeekDays: recurring?.customDays ?? [],
    recurCustomDays: recurring?.customDays ?? [],
    recurMonthMode: recurring?.monthMode ?? 'dayOfMonth',
    recurMonthDay: monthlyDay && monthlyDay <= 31 ? monthlyDay : null,
    recurMonthLastDay: monthlyDay === 32,
    recurMonthDayOfWeek: recurring?.monthDayOfWeek ?? null,
    recurMonthDayOfWeekOccurrence: recurring?.monthDayOfWeekOccurrence ?? 1,
    billingMode: recurring?.billingMode ?? 'PER_VISIT',
    recurBaseAmount: recurring?.baseAmount != null ? String(recurring.baseAmount) : '',
    recurCadence: recurring?.billingCadence?.freq ?? 'MONTHLY',
    // A new subscription invoices on the last day of the month unless told otherwise.
    recurAnchorDay: recurring?.billingAnchorDay != null ? String(recurring.billingAnchorDay) : '31',
    recurTermMonths: recurring?.termMonths != null ? String(recurring.termMonths) : '',
    recurEscalationType: escalation?.type ?? 'NONE',
    recurEscalationValue: escalation?.value != null ? String(escalation.value) : '',
    recurProrationPolicy: recurring?.prorationPolicy ?? 'DAILY',
    recurSeasonStart: recurring?.seasonStart,
    recurSeasonEnd: recurring?.seasonEnd,
    recurHolidayPolicy: recurring?.holidayPolicy,
    recurAutoRenew: recurring?.autoRenew,
    recurPauseBillingPolicy: recurring?.pauseBillingPolicy,
    recurCancellationFeePolicy: recurring?.cancellationFeePolicy,
    recurMinimumTermMonths: recurring?.minimumTermMonths,
    recurCancellationFee: recurring?.cancellationFee,
  };
}
